/**
 * useEditBusiness ViewModel Hook (Refactored)
 * Now uses composition pattern with smaller, focused hooks
 * Reduced from 400+ lines to ~150 lines
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchBusinessById } from '../domain/useCases/Business/fetchBusinessById.js';
import { editBusiness } from '../domain/useCases/Business/editBusiness.js';
import { LocationResolver } from '../domain/services/LocationResolver.js';
import { storageService } from '../domain/services/StorageService.js';
import { normalizeError, withRetry } from '../domain/utils/Error/normalizeError.js';
import { useFormState } from './useFormState.js';
import { useLocationPicker } from './useLocationPicker.js';
import { useHoursManager } from './useHoursManager.js';
import { useUpload } from './useUpload.js';

const INITIAL_FORM_DATA = {
  tag: '',
  grouped: '',
  tagLine: '',
  contactNumber: '',
  landline: '',
  email: '',
  website: '',
  mapAddress: {
    street: '',
    district: '',
    province: '',
    municipality: '',
    wardNo: '',
    generalAddress: '',
    additionalInfo: '',
  },
  buildingInformation: '',
  floorNo: '',
  avatar: '',
  coverImageUrl: '',
  buildingImageUrl: '',
  gallery: [],
  description: '',
  isPickup: false,
  isDelivery: false,
  available: false,
};

export const useEditBusiness = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // Use composed hooks
  const { formData, setFormData, handleChange, handleNestedObjectChange } = useFormState(INITIAL_FORM_DATA);
  const { mapCenter, setMapCenter, searchQuery, setSearchQuery, getLocationFromCoordinates, handleSearchLocation, cleanup: cleanupLocation } = useLocationPicker((locationData) => {
    console.log('useLocationPicker callback received:', locationData);
    console.log('Updating generalAddress to:', locationData.mapAddress?.generalAddress);
    
    setFormData(prev => {
      // Preserve existing generalAddress if the new locationData doesn't provide one
      const updatedMapAddress = {
        ...prev.mapAddress,
        ...locationData.mapAddress,
      };
      
      // If locationData.mapAddress.generalAddress is undefined, keep the existing one
      if (locationData.mapAddress?.generalAddress === undefined) {
        updatedMapAddress.generalAddress = prev.mapAddress?.generalAddress || '';
      }
      
      console.log('Final mapAddress after update:', updatedMapAddress);
      
      return {
        ...prev,
        latitude: locationData.latitude,
        longitude: locationData.longitude,
        mapAddress: updatedMapAddress,
      };
    });
  });
  const { workingDaysAndHours, setWorkingDaysAndHours, handleHoursChange, applyHoursToAllDays, formatTime } = useHoursManager({});
  const { uploadSingleFile, uploadMultipleFiles, uploading: uploadingImage } = useUpload();

  // Local state
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [showHoursModal, setShowHoursModal] = useState(false);
  const [newGalleryUrl, setNewGalleryUrl] = useState('');

  const avatarInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const buildingInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // Initialize LocationResolver
  useEffect(() => {
    LocationResolver.initialize();
  }, []);

  // Parse working hours from string format to object format
  // Converts "9:0-17:0" to { open: "09:00", close: "17:00" }
  const parseWorkingHours = (hoursData) => {
    if (!hoursData) return {};
    const parsed = {};
    Object.entries(hoursData).forEach(([day, timeStr]) => {
      if (typeof timeStr === 'string' && timeStr.includes('-')) {
        const [open, close] = timeStr.split('-').map(t => t.trim());
        // Format time to HH:MM (e.g., "9:0" becomes "09:00")
        const formatTimeForInput = (time) => {
          const parts = time.split(':');
          if (parts.length === 2) {
            return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
          }
          return time;
        };
        parsed[day] = { 
          open: formatTimeForInput(open), 
          close: formatTimeForInput(close) 
        };
      } else if (typeof timeStr === 'object') {
        parsed[day] = timeStr;
      }
    });
    return parsed;
  };

  // Auth guard + initial data fetch
  useEffect(() => {
    const token = storageService.getAuthToken();
    if (!token) {
      navigate('/login');
      return;
    }
    fetchBusiness();
  }, [id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupLocation();
    };
  }, [cleanupLocation]);

  const fetchBusiness = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await withRetry(() => fetchBusinessById(id));
      setBusiness(data);

      // Parse working hours from string format
      const parsedHours = parseWorkingHours(data.workingDaysAndHours);

      // Extract coordinates from API response
      // Priority: Use coordinates array (correct format), ignore separate lat/lng fields (they're swapped in old data)
      let latitude = '';
      let longitude = '';
      
      if (data.location?.coordinates && Array.isArray(data.location.coordinates) && data.location.coordinates.length === 2) {
        // Use coordinates array directly (already in correct format: [lat, lng])
        latitude = data.location.coordinates[0];  // First element is latitude (27.x)
        longitude = data.location.coordinates[1]; // Second element is longitude (85.x)
      } else if (data.latitude && data.longitude) {
        // Fallback to separate fields only if coordinates array doesn't exist
        // Note: These fields are SWAPPED in the database (latitude contains lng, longitude contains lat)
        latitude = data.longitude;  // Swap: get lat from longitude field
        longitude = data.latitude;  // Swap: get lng from latitude field
      }

      // Set map center to the exact pinned location
      if (latitude && longitude) {
        setMapCenter([parseFloat(latitude), parseFloat(longitude)]);
      }

      // Populate form data
      console.log('Business data from API:', data);
      console.log('General Address from API:', data.mapAddress?.generalAddress);
      console.log('Address field from API:', data.address);
      
      // Prioritize generalAddress over address field for consistency
      const correctAddress = data.mapAddress?.generalAddress || data.address || '';
      console.log('Using correct address:', correctAddress);
      
      setFormData({
        tag: data.tag || '',
        grouped: data.name || '',
        tagLine: data.tagLine || '',
        contactNumber: data.contact || data.contactNumber || '',
        landline: data.landline || '',
        email: data.email || '',
        website: data.website || '',
        mapAddress: {
          street: data.mapAddress?.street || '',
          district: data.mapAddress?.district || '',
          province: data.mapAddress?.province || '',
          municipality: data.mapAddress?.municipality || '',
          wardNo: data.mapAddress?.wardNo || '',
          generalAddress: correctAddress, // Use the prioritized address
          additionalInfo: data.mapAddress?.additionalInfo || '',
        },
        latitude: latitude.toString(),
        longitude: longitude.toString(),
        buildingInformation: data.buildingInformation || '',
        floorNo: data.floorNo || '',
        avatar: data.avatar || '',
        coverImageUrl: data.coverImageUrl || '',
        buildingImageUrl: data.buildingImageUrl || '',
        gallery: data.gallery || [],
        description: data.description || '',
        isPickup: data.pickup || false,
        isDelivery: data.delivery || false,
        available: data.available || false,
      });

      console.log('Form data after setting:', {
        generalAddress: data.mapAddress?.generalAddress,
        formDataMapAddress: {
          generalAddress: data.mapAddress?.generalAddress
        }
      });

      // Set parsed working hours to the hours manager
      if (Object.keys(parsedHours).length > 0) {
        setWorkingDaysAndHours(parsedHours);
      }
    } catch (err) {
      setError(normalizeError(err, 'business'));
    } finally {
      setLoading(false);
    }
  };

  // Gallery handlers
  const handleAddGalleryImage = () => {
    if (newGalleryUrl.trim() && formData.gallery.length < 5) {
      setFormData(prev => ({
        ...prev,
        gallery: [...prev.gallery, newGalleryUrl.trim()],
      }));
      setNewGalleryUrl('');
    }
  };

  const handleRemoveGalleryImage = (index) => {
    setFormData(prev => ({
      ...prev,
      gallery: prev.gallery.filter((_, i) => i !== index),
    }));
  };

  // Image upload handlers
  const handleImageUpload = async (e, fieldName) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const compressed = await uploadSingleFile(file);
      setFormData(prev => ({ ...prev, [fieldName]: compressed }));
    } catch (err) {
      setError(normalizeError(err, 'image upload'));
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remaining = 5 - formData.gallery.length;
    const filesToProcess = files.slice(0, remaining);

    try {
      // Upload each file one at a time using single file upload (not batch)
      const uploadedUrls = [];
      for (const file of filesToProcess) {
        const url = await uploadSingleFile(file);
        if (!url || typeof url !== 'string' || url.length === 0) {
          throw new Error(`Invalid URL returned for gallery file: ${url}`);
        }
        uploadedUrls.push(url);
      }
      
      setFormData(prev => ({
        ...prev,
        gallery: [...prev.gallery, ...uploadedUrls],
      }));
    } catch (err) {
      setError(normalizeError(err, 'gallery upload'));
    }
  };

  // Submit handler
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Convert working hours from object format back to string format
      const workingHoursPayload = {};
      Object.entries(workingDaysAndHours).forEach(([day, hours]) => {
        if (hours?.open === 'closed' || hours?.close === 'closed') {
          // Day is explicitly closed
          workingHoursPayload[day] = 'closed';
        } else if (hours?.open && hours?.close) {
          // Day has hours set
          workingHoursPayload[day] = `${hours.open}-${hours.close}`;
        }
        // If no hours set at all, don't include in payload
      });

      const payload = {
        tag: formData.tag,
        name: formData.grouped,
        tagLine: formData.tagLine,
        contact: formData.contactNumber,
        landline: formData.landline,
        email: formData.email || null, // Send null if empty instead of empty string
        website: formData.website,
        address: formData.mapAddress.generalAddress || '', // Sync address with generalAddress
        mapAddress: {
          street: formData.mapAddress.street || '',
          district: formData.mapAddress.district ? String(formData.mapAddress.district) : '',
          province: formData.mapAddress.province ? String(formData.mapAddress.province) : '',
          municipality: formData.mapAddress.municipality ? String(formData.mapAddress.municipality) : '',
          wardNo: formData.mapAddress.wardNo ? String(formData.mapAddress.wardNo) : '',
          generalAddress: formData.mapAddress.generalAddress || '',
          additionalInfo: formData.mapAddress.additionalInfo || '',
        },
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        buildingInformation: formData.buildingInformation,
        floorNo: formData.floorNo,
        avatar: formData.avatar,
        coverImageUrl: formData.coverImageUrl,
        buildingImageUrl: formData.buildingImageUrl,
        gallery: formData.gallery,
        description: formData.description,
        workingDaysAndHours: workingHoursPayload,
        pickup: formData.isPickup,
        delivery: formData.isDelivery,
        available: formData.available,
      };

      // Debug: Log the payload to see what's being sent
      console.log('Edit Business Payload:', JSON.stringify(payload, null, 2));
      console.log('General Address being sent:', payload.mapAddress.generalAddress);
      console.log('MapAddress fields being sent:', {
        district: payload.mapAddress.district,
        districtType: typeof payload.mapAddress.district,
        province: payload.mapAddress.province,
        provinceType: typeof payload.mapAddress.province,
        municipality: payload.mapAddress.municipality,
        municipalityType: typeof payload.mapAddress.municipality,
        wardNo: payload.mapAddress.wardNo,
        wardNoType: typeof payload.mapAddress.wardNo,
      });

      await withRetry(() => editBusiness(id, payload));
      setSuccess('Business updated successfully!');

      setTimeout(() => {
        navigate('/dashboard?tab=my-businesses');
      }, 2000);
    } catch (err) {
      setError(normalizeError(err, 'business update'));
    } finally {
      setLoading(false);
    }
  };

  return {
    // State
    business,
    loading,
    error,
    success,
    formData,
    showLocationPicker,
    showHoursModal,
    newGalleryUrl,
    mapCenter,
    searchQuery,
    uploadingImage,
    workingDaysAndHours,
    // Refs
    avatarInputRef,
    coverInputRef,
    buildingInputRef,
    galleryInputRef,
    // Handlers
    handleChange,
    handleNestedObjectChange,
    handleLocationSelect: () => {}, // Kept for compatibility
    getLocationFromCoordinates,
    handleAddGalleryImage,
    handleRemoveGalleryImage,
    handleHoursChange,
    applyHoursToAllDays,
    handleImageUpload,
    handleGalleryUpload,
    handleSearchLocation,
    handleSubmit,
    formatTime,
    // Setters
    setFormData,
    setShowLocationPicker,
    setShowHoursModal,
    setNewGalleryUrl,
    setSearchQuery,
    setWorkingDaysAndHours,
    navigate,
  };
};
